import { useEffect, useState, useCallback } from "react";
import { useRecordQuotesStore } from "./quotesStore";
import QuoteEditor, { QuoteEditorData } from "./QuoteEditor";
import { Loader2 } from "lucide-react";
import {
  QuoteResponse,
  CreateQuoteParams,
  UpdateQuoteParams,
  QuoteStatusEnum,
  QuoteLineTypeValues,
  HydratedQuoteLine,
  Permission,
} from "@forgepoint/shared";
import { toast } from "sonner";
import { useQuoteSettingsStore } from "@/store/quoteSettingsStore";
import { usePriceListsStore } from "@/modules/priceLists/priceListsStore";
import { useHasAnyPermission } from "@/store/authStore";
import { useCanEditQuote } from "./useCanEditQuote";
import { usePendingFinalizeTask } from "./usePendingFinalizeTask";
import { getDateOnlyParam } from "./mergeQuoteState";
import { hasUnsavedLines } from "./utils";

interface QuoteEditorControllerProps {
  quoteId?: string;
  onSaved?: (quote: QuoteResponse) => void;
  onCancel?: () => void;
  onArchived?: () => void;
  onNavigateToQuote?: (quoteId: string) => void;
}

// Extract editable data from a quote response
const quoteToEditorData = (quote: QuoteResponse): QuoteEditorData => ({
  name: quote.name || "",
  customerCompanyId: quote.customerCompanyId || null,
  priceListId: quote.priceListId || null,
  shippingAddressId: quote.shippingAddressId || null,
  locationId: quote.locationId || null,
  opportunityId: quote.opportunityId || null,
  salesRepUserId: quote.salesRepUserId ?? null,
  personIds: (quote.people ?? []).map((person) => person.id),
  deliveryTerms: quote.deliveryTerms || "",
  paymentTerms: quote.paymentTerms || "",
  termsOfService: quote.termsOfService || "",
  quoteFooter: quote.quoteFooter || "",
  expirationDate: quote.expirationDate ?? "",
  quoteDate: quote.quoteDate ?? "",
  showTotals: quote.showTotals ?? true,
  coverLetterMarkdown: quote.coverLetterMarkdown || "",
  customFields: quote.customFields ?? {},
  quoteCurrency: quote.quoteCurrency ?? null,
  snapshot: quote.snapshot ?? null,
  lines: (quote.lines || []).map((line) => {
    // Strip hydrated fields, keep only editable fields (preserve id)
    if (line.type === QuoteLineTypeValues.ITEM) {
      const itemLine = line;
      // Strip computed fields from quantityBreaks
      const editableBreaks = itemLine.quantityBreaks.map((qb) => ({
        quantity: qb.quantity,
        ...(qb.unitPriceOverride !== null && qb.unitPriceOverride !== undefined
          ? { unitPriceOverride: qb.unitPriceOverride }
          : {}),
      }));
      return {
        id: itemLine.id,
        type: itemLine.type,
        itemId: itemLine.itemId,
        description: itemLine.description,
        confidenceScore: itemLine.confidenceScore,
        reasoning: itemLine.reasoning,
        quantityBreaks: editableBreaks,
        customFields: itemLine.customFields,
        itemOverrides: itemLine.itemOverrides,
        unitOverride: itemLine.unitOverride,
        dimensionUnitOverride: itemLine.dimensionUnitOverride,
      };
    }
    // For section/note/discount lines, preserve id
    return {
      ...line,
      id: line.id,
    };
  }),
});

// Create empty editor data for a new quote
const createEmptyEditorData = (): QuoteEditorData => ({
  name: "",
  customerCompanyId: null,
  priceListId: null,
  shippingAddressId: null,
  locationId: null,
  opportunityId: null,
  salesRepUserId: null,
  personIds: [],
  deliveryTerms: "",
  paymentTerms: "",
  termsOfService: "",
  quoteFooter: "",
  expirationDate: "",
  quoteDate: "",
  lines: [],
  showTotals: true,
  coverLetterMarkdown: "",
  customFields: {},
  quoteCurrency: null,
  snapshot: null,
});

const getOptionalExpirationDateParam = (expirationDate: string) =>
  expirationDate ? expirationDate : undefined;

export function QuoteEditorController({
  quoteId,
  onSaved,
  onCancel,
  onArchived,
  onNavigateToQuote,
}: QuoteEditorControllerProps) {
  const {
    getQuote,
    createQuote,
    updateQuote,
    finalizeQuote,
    createRevision,
    archiveQuote,
    unarchiveQuote,
    quoteLoadingStates,
  } = useRecordQuotesStore();

  const quoteSettings = useQuoteSettingsStore((state) => state.settings);
  const fetchQuoteSettings = useQuoteSettingsStore(
    (state) => state.fetchSettings,
  );
  const fetchCurrentPriceListForCompany = usePriceListsStore(
    (state) => state.fetchCurrentPriceListForCompany,
  );
  const showLocationField = quoteSettings?.showLocationField ?? false;

  const [quote, setQuote] = useState<QuoteResponse | null>(null);
  const pendingFinalizeTask = usePendingFinalizeTask(
    quoteId,
    quote?.opportunityId,
  );
  const [localData, setLocalData] = useState<QuoteEditorData>(
    createEmptyEditorData(),
  );
  const [hydratedLines, setHydratedLines] = useState<HydratedQuoteLine[]>([]);
  const [isDirty, setIsDirty] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [initializedFromSettings, setInitializedFromSettings] = useState(false);

  // Fetch quote settings (needed for defaults on new quotes and customFieldsSchema on all quotes)
  useEffect(() => {
    if (!quoteSettings) {
      fetchQuoteSettings();
    }
  }, [quoteSettings, fetchQuoteSettings]);

  // Apply quote settings defaults for new quotes
  useEffect(() => {
    if (quoteId || !quoteSettings || initializedFromSettings) {
      return;
    }

    setLocalData((current) => ({
      ...current,
      deliveryTerms: current.deliveryTerms || quoteSettings.deliveryTerms || "",
      paymentTerms: current.paymentTerms || quoteSettings.paymentTerms || "",
      termsOfService:
        current.termsOfService || quoteSettings.termsAndConditions || "",
      quoteFooter: current.quoteFooter || quoteSettings.quoteFooter || "",
      coverLetterMarkdown: current.coverLetterMarkdown || "",
    }));
    setInitializedFromSettings(true);
  }, [quoteId, quoteSettings, initializedFromSettings]);

  useEffect(() => {
    const loadQuote = async () => {
      if (!quoteId) {
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const fetchedQuote = await getQuote(quoteId);
        if (fetchedQuote) {
          setQuote(fetchedQuote);
          setLocalData(quoteToEditorData(fetchedQuote));
          setHydratedLines(fetchedQuote.lines || []);
          setIsDirty(false);
        } else {
          setError("Quote not found");
        }
      } catch (_err) {
        setError("Failed to load quote");
      } finally {
        setLoading(false);
      }
    };

    loadQuote();
  }, [quoteId, getQuote]);

  const handleDataChange = useCallback(
    async (newData: QuoteEditorData) => {
      if (newData.customerCompanyId !== localData.customerCompanyId) {
        const companyId = newData.customerCompanyId;
        if (!companyId) {
          setLocalData({ ...newData, priceListId: null });
          setIsDirty(true);
          return;
        }
        setLocalData({
          ...newData,
          priceListId: await fetchCurrentPriceListForCompany(companyId),
        });
        setIsDirty(true);
        return;
      }

      setLocalData(newData);
      setIsDirty(true);
    },
    [fetchCurrentPriceListForCompany, localData.customerCompanyId],
  );

  const handleSave = useCallback(async () => {
    const requiresCustomerSignature =
      quote?.requiresCustomerSignature ?? quoteSettings?.useSignatures ?? false;

    const baseParams = {
      name: localData.name || undefined,
      customerCompanyId:
        localData.customerCompanyId === ""
          ? undefined
          : localData.customerCompanyId,
      priceListId: localData.priceListId,
      shippingAddressId: localData.shippingAddressId,
      ...(showLocationField ? { locationId: localData.locationId } : {}),
      opportunityId:
        localData.opportunityId === "" ? undefined : localData.opportunityId,
      salesRepUserId: localData.salesRepUserId,
      personIds: localData.personIds,
      deliveryTerms: localData.deliveryTerms,
      paymentTerms: localData.paymentTerms,
      termsOfService: localData.termsOfService,
      quoteFooter: localData.quoteFooter,
      quoteDate: getDateOnlyParam(localData.quoteDate),
      showTotals: localData.showTotals,
      customFields: localData.customFields,
      coverLetterMarkdown: localData.coverLetterMarkdown || null,
      quoteCurrency: localData.quoteCurrency,
    };
    const savedEditorData = quote ? quoteToEditorData(quote) : null;

    let result: QuoteResponse | null;
    let saveError: string | null = null;

    if (quoteId) {
      const params: UpdateQuoteParams = {
        ...baseParams,
        ...(hasUnsavedLines(localData, savedEditorData)
          ? { lines: localData.lines }
          : {}),
        expirationDate: getDateOnlyParam(localData.expirationDate),
      };
      const updateResult = await updateQuote(quoteId, params);
      result = updateResult.quote;
      saveError = updateResult.error;
    } else {
      const params: CreateQuoteParams = {
        ...baseParams,
        lines: localData.lines,
        expirationDate: getOptionalExpirationDateParam(
          localData.expirationDate,
        ),
        requiresCustomerSignature,
      };
      const createResult = await createQuote(params);
      result = createResult.quote;
      saveError = createResult.error;
    }

    if (result) {
      toast.success(
        quoteId ? "Quote updated successfully" : "Quote created successfully",
      );
      onSaved?.(result);
      setQuote(result);
      setLocalData(quoteToEditorData(result));
      setHydratedLines(result.lines || []);
      setIsDirty(false);
    } else {
      toast.error(
        saveError ||
          (quoteId ? "Failed to update quote" : "Failed to create quote"),
      );
    }
  }, [
    quoteId,
    quote,
    localData,
    quoteSettings,
    showLocationField,
    createQuote,
    updateQuote,
    onSaved,
  ]);

  const handleFinalize = useCallback(async () => {
    if (!quoteId || !quote) {
      return;
    }

    const params: UpdateQuoteParams = {
      name: localData.name || undefined,
      customerCompanyId:
        localData.customerCompanyId === ""
          ? undefined
          : localData.customerCompanyId,
      priceListId: localData.priceListId,
      shippingAddressId: localData.shippingAddressId,
      ...(showLocationField ? { locationId: localData.locationId } : {}),
      opportunityId:
        localData.opportunityId === "" ? undefined : localData.opportunityId,
      salesRepUserId: localData.salesRepUserId,
      personIds: localData.personIds,
      deliveryTerms: localData.deliveryTerms,
      paymentTerms: localData.paymentTerms,
      termsOfService: localData.termsOfService,
      quoteFooter: localData.quoteFooter,
      expirationDate: getDateOnlyParam(localData.expirationDate),
      quoteDate: getDateOnlyParam(localData.quoteDate),
      lines: localData.lines,
      showTotals: localData.showTotals,
      customFields: localData.customFields,
      coverLetterMarkdown: localData.coverLetterMarkdown || null,
      quoteCurrency: localData.quoteCurrency,
    };

    const savedQuote = await updateQuote(quoteId, params);
    if (!savedQuote.quote) {
      toast.error(
        savedQuote.error || "Failed to save quote before finalizing",
        {
          duration: 10000,
        },
      );
      return;
    }

    const result = await finalizeQuote(quoteId);
    if (!result.quote) {
      toast.error(result.error ?? "Failed to finalize quote", {
        duration: 10000,
      });
      return;
    }

    toast.success("Quote finalized successfully");
    setQuote(result.quote);
    setHydratedLines(result.quote.lines || []);
    setIsDirty(false);
  }, [
    quoteId,
    quote,
    localData,
    updateQuote,
    finalizeQuote,
    showLocationField,
  ]);

  const handleCreateRevision = useCallback(async () => {
    if (!quoteId || !quote) {
      return;
    }

    const result = await createRevision(quoteId);
    if (result.quote) {
      toast.success("New revision created successfully");
      onSaved?.(result.quote);
      onNavigateToQuote?.(result.quote.id);
      return;
    }

    if (result.existingDraftId) {
      const draftId = result.existingDraftId;
      toast.error("A draft revision already exists for this quote", {
        action: onNavigateToQuote
          ? {
              label: "View Draft",
              onClick: () => onNavigateToQuote(draftId),
            }
          : undefined,
      });
      return;
    }

    toast.error("Failed to create revision");
  }, [quoteId, quote, createRevision, onSaved, onNavigateToQuote]);

  const handleRefresh = useCallback(async () => {
    if (!quoteId) {
      return;
    }

    const fetchedQuote = await getQuote(quoteId);
    if (fetchedQuote) {
      setQuote(fetchedQuote);
      setHydratedLines(fetchedQuote.lines || []);
      // Resync editor data on finalized/archived quotes — no unsaved local edits.
      if (
        fetchedQuote.status === QuoteStatusEnum.FINALIZED ||
        fetchedQuote.archivedAt
      ) {
        setLocalData(quoteToEditorData(fetchedQuote));
        setIsDirty(false);
      }
    }
  }, [quoteId, getQuote]);

  /**
   * Archives the current quote and refreshes local state.
   */
  const handleArchive = useCallback(async () => {
    if (!quoteId) {
      return;
    }

    const result = await archiveQuote(quoteId);
    if (result.success) {
      toast.success("Quote archived");
      const fetchedQuote = await getQuote(quoteId);
      if (fetchedQuote) {
        setQuote(fetchedQuote);
        setLocalData(quoteToEditorData(fetchedQuote));
        setHydratedLines(fetchedQuote.lines || []);
        setIsDirty(false);
      }
      onArchived?.();
    } else {
      toast.error(result.error || "Failed to archive quote");
    }
  }, [quoteId, archiveQuote, getQuote, onArchived]);

  /**
   * Unarchives the current quote and refreshes local state.
   */
  const handleUnarchive = useCallback(async () => {
    if (!quoteId) {
      return;
    }

    const result = await unarchiveQuote(quoteId);
    if (result.success) {
      toast.success("Quote unarchived");
      const fetchedQuote = await getQuote(quoteId);
      if (fetchedQuote) {
        setQuote(fetchedQuote);
        setLocalData(quoteToEditorData(fetchedQuote));
        setHydratedLines(fetchedQuote.lines || []);
        setIsDirty(false);
      }
      onArchived?.();
    } else {
      toast.error(result.error || "Failed to unarchive quote");
    }
  }, [quoteId, unarchiveQuote, getQuote, onArchived]);

  const loadingState = quote?.id
    ? quoteLoadingStates.get(quote.id)
    : quoteLoadingStates.get("new");
  const isSaving = loadingState?.isLoadingSave || false;
  const isLoading = loading || loadingState?.isLoadingGet;
  const isFinalized = quote?.status === QuoteStatusEnum.FINALIZED;
  const isArchived = !!quote?.archivedAt;

  // A new quote (no id) only needs any edit primitive since the creator owns it.
  const canEditExistingQuote = useCanEditQuote(quote);
  const canCreateQuote = useHasAnyPermission(
    Permission.QUOTES_EDIT_OWN,
    Permission.QUOTES_EDIT_ALL,
  );
  const canEdit = quoteId ? canEditExistingQuote : canCreateQuote;

  if (isLoading && !quote) {
    return (
      <div className="flex items-center justify-center h-full p-4">
        <Loader2 className="h-6 w-6 sm:h-8 sm:w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full p-4">
        <p className="text-red-500 text-sm sm:text-base text-center">{error}</p>
      </div>
    );
  }

  return (
    <QuoteEditor
      quote={quote}
      data={localData}
      hydratedLines={hydratedLines}
      isDirty={isDirty}
      readOnly={isFinalized || isArchived || !canEdit}
      canManagePublicAccess={canEdit}
      canEditOpportunity={canEdit && !isArchived}
      isSaving={isSaving}
      showTotalWeight={quoteSettings?.showTotalWeight ?? false}
      enableCoverLetter={
        quote?.enableCoverLetter ?? quoteSettings?.enableCoverLetter ?? false
      }
      showLocationField={showLocationField}
      customFieldsSchema={quoteSettings?.customFieldsSchema}
      onDataChange={handleDataChange}
      onSave={handleSave}
      onCancel={onCancel}
      onFinalize={canEdit ? handleFinalize : undefined}
      finalizeReviewThreadId={
        canEdit ? (pendingFinalizeTask?.threadId ?? undefined) : undefined
      }
      onCreateRevision={canEdit ? handleCreateRevision : undefined}
      onRefresh={handleRefresh}
      onArchive={quoteId && canEdit ? handleArchive : undefined}
      onUnarchive={quoteId && canEdit ? handleUnarchive : undefined}
      isArchiving={loadingState?.isLoadingArchive}
    />
  );
}
